import { CHECKS, checkByKey } from './selfRegularization.js';

/**
 * What the regularization screens should work out for the admin, so nobody has to.
 *
 * "Who needs what" across the vendor base: which records lack an input a registry check
 * needs (the vendor has to supply it — through the vendor), which carry the input but were
 * never confirmed (a run can do it — self-regularization), which links have gone unanswered
 * (remind) or expired (resend). And what an ask turns into for the vendor: the subject line,
 * the sections, the minutes it takes.
 */

const DAY = 86_400_000;

/** A link unanswered this long earns a reminder. */
export const REMIND_AFTER_DAYS = 3;

/** What a vendor can be asked to do through the link. `supplies` is the record input it fills. */
export const ASKS = [
  {
    key: 'gst',
    label: 'Verify GST registration',
    short: 'GST',
    icon: 'receipt_long',
    minutes: 2,
    supplies: 'gst_number',
    phrase: 'verify your GST registration',
    blurb: 'They confirm the GSTIN; the form pulls the registry record and locks the legal name and address to it.',
  },
  {
    key: 'pan',
    label: 'Verify PAN',
    short: 'PAN',
    icon: 'badge',
    minutes: 1,
    supplies: 'pan_number',
    phrase: 'verify your PAN',
    blurb: 'The PAN is read off their verified GSTIN, so they only confirm it — no separate income-tax lookup.',
  },
  {
    key: 'bank',
    label: 'Give bank details',
    short: 'Bank',
    icon: 'account_balance',
    minutes: 4,
    supplies: 'bank',
    phrase: 'share your bank details',
    blurb: 'They upload a cancelled cheque — read by OCR — or type the account; a ₹1 check confirms the account and the name on it.',
  },
  {
    key: 'udyam',
    label: 'Give MSME / Udyam registration',
    short: 'Udyam',
    icon: 'storefront',
    minutes: 3,
    supplies: 'msme_number',
    phrase: 'share your MSME / Udyam registration',
    blurb: 'They enter the Udyam number; the form pulls the classification. An OTP unlocks the exact turnover.',
  },
  {
    key: 'documents',
    label: 'Upload documents',
    short: 'Documents',
    icon: 'upload_file',
    minutes: 5,
    supplies: null,
    phrase: 'upload your documents',
    blurb: 'GST certificate, PAN card, cancelled cheque, Udyam certificate — each read by OCR and cross-checked against what they typed.',
  },
  {
    key: 'full',
    label: 'The whole form',
    short: 'Whole form',
    icon: 'list_alt',
    minutes: 15,
    supplies: null,
    phrase: 'complete your vendor details',
    blurb: 'Every section, start to finish — for a vendor whose record needs a fresh start.',
  },
];

export function askByKey(key) {
  return ASKS.find((a) => a.key === key);
}

/** Recent vendors verify what they gave; legacy vendors complete what the new form added. */
export function defaultAsksFor(segment) {
  if (segment === 'recent') return ['gst', 'pan', 'bank'];
  if (segment === 'legacy') return ['udyam', 'bank', 'documents'];
  return ['full'];
}

/** The email an ask becomes: one subject, a short list of sections, and how long it takes. */
export function emailFor(segment, askKeys) {
  const keys = askKeys.includes('full') ? ['full'] : ASKS.filter((a) => askKeys.includes(a.key)).map((a) => a.key);
  const asks = keys.map(askByKey);
  const minutes = asks.reduce((n, a) => n + a.minutes, 0);
  const subject =
    asks.length === 1
      ? `Please ${asks[0].phrase} with Amber`
      : segment === 'recent'
        ? 'Please confirm your vendor details with Amber'
        : 'Please complete your vendor details for Amber';
  const lead =
    segment === 'recent'
      ? 'We are confirming the details we hold for your company.'
      : 'Our vendor requirements have changed and your record needs a few additions.';
  return {
    subject,
    lead: `${lead} Open the link and complete the section${asks.length === 1 ? '' : 's'} below — about ${minutes} minute${minutes === 1 ? '' : 's'}.`,
    sections: asks.map((a) => a.label),
    minutes,
  };
}

const stateFor = (stateOf, v) => stateOf(v) || { inputs: [], verified: new Set() };

/**
 * The picture of the whole base, per check: confirmed by a registry, on record but never
 * confirmed, or missing altogether — which only the vendor can fix.
 */
export function baseHealth(vendors, stateOf) {
  const checks = {};
  for (const c of CHECKS) checks[c.key] = { verified: 0, unverified: 0, missing: 0 };
  let fully = 0;
  let nothing = 0;
  for (const v of vendors) {
    const st = stateFor(stateOf, v);
    let allOk = true;
    let any = false;
    for (const c of CHECKS) {
      // Verified is verified: a registry answered once, whatever the record can supply today.
      if (st.verified.has(c.key)) {
        checks[c.key].verified += 1;
        any = true;
      } else if (!st.inputs.includes(c.needs)) {
        checks[c.key].missing += 1;
        allOk = false;
      } else {
        checks[c.key].unverified += 1;
        allOk = false;
      }
    }
    if (allOk) fully += 1;
    if (!any) nothing += 1;
  }
  return { total: vendors.length, checks, fully_verified: fully, nothing_verified: nothing };
}

const isOpen = (link) => !!link && (link.status === 'sent' || link.status === 'submitted');
const ageDays = (link, now) => (now - Date.parse(link.at || 0)) / DAY;

/**
 * What to ask, and whom, through the vendor: every record missing an input (skipping anyone
 * already asked and waiting), links unanswered long enough to remind, expired links to resend,
 * and vendors never regularized at all. `linkOf(id)` is the vendor's newest link or null.
 */
export function suggestAsks(vendors, stateOf, linkOf, now = Date.now()) {
  const out = [];
  const remind = [];
  const resend = [];
  const never = [];
  const missing = new Map(CHECKS.map((c) => [c.key, []]));
  for (const v of vendors) {
    const link = linkOf(v.id);
    const st = stateFor(stateOf, v);
    if (link?.status === 'sent' && ageDays(link, now) >= REMIND_AFTER_DAYS) remind.push(v.id);
    if (link?.status === 'expired') resend.push(v.id);
    if (!link && st.verified.size === 0) never.push(v.id);
    if (isOpen(link)) continue;
    // A local check reads another check's input (PAN reads the GSTIN): that check's ask covers it.
    for (const c of CHECKS) if (!c.local && !st.inputs.includes(c.needs)) missing.get(c.key).push(v.id);
  }
  if (remind.length) {
    out.push({ key: 'remind', kind: 'remind', icon: 'notifications_active', title: `No answer for ${REMIND_AFTER_DAYS}+ days`, detail: 'Send a reminder — the same link, one nudge.', vendorIds: remind, asks: [] });
  }
  if (resend.length) {
    out.push({ key: 'resend', kind: 'resend', icon: 'replay', title: 'Link expired unanswered', detail: 'Send a fresh link for the same ask.', vendorIds: resend, asks: [] });
  }
  for (const c of CHECKS) {
    const ids = missing.get(c.key);
    if (!ids.length) continue;
    const ask = askByKey(c.key);
    out.push({
      key: `missing:${c.key}`,
      kind: 'ask',
      icon: ask.icon,
      title: `No ${c.needsLabel}`,
      detail: `Ask them to ${ask.phrase.replace('your ', 'their ')} — a run cannot verify what is not on file.`,
      vendorIds: ids,
      asks: [c.key],
    });
  }
  if (never.length) {
    out.push({ key: 'never', kind: 'ask', icon: 'restart_alt', title: 'Never regularized', detail: 'Nothing verified and never asked — send the whole form.', vendorIds: never, asks: ['full'] });
  }
  const rank = (s) => (s.kind === 'remind' ? 0 : s.kind === 'resend' ? 1 : 2);
  return out.sort((a, b) => rank(a) - rank(b) || b.vendorIds.length - a.vendorIds.length);
}

/**
 * What to run, and on whom: per check, everyone whose input is on record and never confirmed, with
 * the price. `priceOf(key)` is the server's rate card; the catalogue's placeholder otherwise.
 */
export function suggestRuns(vendors, stateOf, priceOf = (key) => checkByKey(key).price_inr) {
  const out = [];
  for (const c of CHECKS) {
    const ids = [];
    for (const v of vendors) {
      const st = stateFor(stateOf, v);
      if (st.inputs.includes(c.needs) && !st.verified.has(c.key)) ids.push(v.id);
    }
    if (!ids.length) continue;
    const needs = `${c.needsLabel[0].toUpperCase()}${c.needsLabel.slice(1)}`;
    out.push({
      key: `run:${c.key}`,
      kind: 'run',
      icon: c.icon,
      title: `${c.label} not yet verified`,
      detail: c.local ? `${needs}, ${c.label} never taken from it. Free, no registry call.` : `${needs}, never confirmed by the registry.`,
      checks: [c.key],
      vendorIds: ids,
      cost_inr: c.local ? 0 : Math.round(ids.length * Number(priceOf(c.key) || 0) * 100) / 100,
    });
  }
  return out.sort((a, b) => b.vendorIds.length - a.vendorIds.length);
}
